import { SchemaField } from "../featuer-layer-settings/schema"

/**
 * Calculates how 'completed' a set of attributes are against a schema.
 * @param fields The schema fields to validate against
 * @param attributes The feature's current attributes
 * @returns number a percentage [0, 1] of completed optional fields
 */
export const calculateCompletionScore = (
  fields: SchemaField[],
  attributes: Record<string, any>
) => {
  /** Split the fields into groups */
  const requiredFields = fields.filter(f => f.required)
  const optionalFields = fields.filter(f => !f.required)

  /** Get an required score */
  const requiredValues = requiredFields.reduce(
    (score: number, field: SchemaField) =>
      field.name && attributes[field.name] ? ++score : score,
    0
  )

  /** Get an optional fields score */
  const optionalValues = optionalFields.reduce(
    (score: number, field: SchemaField) =>
      field.name && attributes[field.name] ? ++score : score,
    0
  )

  return requiredValues < requiredFields.length
    ? 0
    : optionalValues / optionalFields.length
}

/**
 * Returns an arcade expression that scores the feature based on the
 * number of required and optional fields that have been filled out.
 * Useful for coloring points on a map based on whether they need
 * work or not.
 *
 * See arcade guide: https://developers.arcgis.com/arcade/guide/
 *
 * @param fields The schema fields to build the expression from
 * @returns string
 */
export const generateCompletionScoreArcadeExpression = (
  fields: SchemaField[]
) => `
var requiredFields = [
  ${fields
    .filter(f => f.required)
    .map(f => `"${f.name}"`)
    .join(",")}
]

var requiredScore = 0;
for(var index in requiredFields){
  if(IsEmpty($feature[requiredFields[index]]) == false){
    requiredScore++
  }
}

// optional fields
var optionalFields = [
  ${fields
    .filter(f => !f.required && f.variant !== "switch")
    .map(f => `"${f.name}"`)
    .join(",")}
]

var optionalScore = 0;
for(var index in optionalFields){
    if(IsEmpty($feature[optionalFields[index]]) == false){
        optionalScore++;
    }
}

if(requiredScore < Count(requiredFields)){
    return -1;
} else {
    return optionalScore / Count(optionalFields)
}
`
